import React from 'react';
import VehicleCard from '../components/VehicleCard';
import ServiceBooking from '../components/ServiceBooking';
import DealershipInfo from '../components/DealershipInfo';
import SmartRecommendations from '../components/SmartRecommendations';
import ServiceAdvisor from '../components/ServiceAdvisor';
import TrustSignals from '../components/TrustSignals';
import CustomerTestimonials from '../components/CustomerTestimonials';
import LiveInventoryCounter from '../components/LiveInventoryCounter';
import PriceMatchGuarantee from '../components/PriceMatchGuarantee';
import LeadCaptureIncentives from '../components/LeadCaptureIncentives';
import ModelComparison from '../components/ModelComparison';
import PricingAlerts from '../components/PricingAlerts';
import LoanCalculator from '../components/LoanCalculator'; // Updated
import { vehicles, getVehiclesByFilter } from '../data/vehicles';
import openaiService from '../services/openaiService';

class AIResponseHandler {
  static async processMessage(message) {
    const lowerMessage = message.toLowerCase();

    // Pricing Alerts Tool
    if (this.isPricingAlertQuery(lowerMessage)) {
      return this.handlePricingAlertQuery(lowerMessage);
    }

    // Model Comparison Tool
    if (this.isComparisonQuery(lowerMessage)) {
      return this.handleComparisonQuery(lowerMessage);
    }

    // Direct inventory query
    if (this.isDirectInventoryQuery(lowerMessage)) {
      return this.handleDirectInventoryQuery(lowerMessage);
    }

    // Smart recommendations
    if (this.isRecommendationQuery(lowerMessage)) {
      return this.handleSmartRecommendations();
    }

    // Service advisor
    if (this.isServiceAdviceQuery(lowerMessage)) {
      return this.handleServiceAdvice();
    }

    // Trust and pricing
    if (this.isTrustQuery(lowerMessage)) return this.handleTrustQuery();
    if (this.isPricingQuery(lowerMessage)) return this.handlePricingQuery();
    if (this.isLeadCaptureQuery(lowerMessage)) return this.handleLeadCapture();

    // General inventory
    if (this.isInventoryQuery(lowerMessage)) {
      return this.handleInventoryQuery(lowerMessage);
    }

    // Vehicle information
    if (this.isVehicleInfoQuery(lowerMessage)) {
      return await this.handleVehicleInfoWithAI(lowerMessage);
    }

    // Service/Finance/Info
    if (this.isServiceQuery(lowerMessage)) return this.handleServiceQuery();
    
    // Updated Finance Query
    if (this.isFinanceQuery(lowerMessage)) {
      return this.handleFinanceQuery(lowerMessage);
    }

    if (this.isDealershipInfoQuery(lowerMessage)) return this.handleDealershipInfo();
    
    if (this.isSpecialsQuery(lowerMessage)) {
      return await this.handleSpecialsWithAI(lowerMessage);
    }

    return await this.getAIResponse(message);
  }

  static isPricingAlertQuery(message) {
    const keywords = ['alert', 'notification', 'price drop', 'cheaper', 'notify', 'track price', 'monitoring'];
    return keywords.some(k => message.includes(k));
  }

  static handlePricingAlertQuery(message) {
    const models = ['f-150', 'civic', 'rav4', 'x5', 'sierra', 'model 3'];
    const foundModel = models.find(m => message.includes(m));

    return {
      content: foundModel 
        ? `I can certainly track the pricing for the ${foundModel.toUpperCase()} for you. Here is our live pricing dashboard:`
        : `I've opened our Live Pricing Dashboard. You can see active price drops and subscribe for instant notifications on any model below:`,
      component: <PricingAlerts targetModel={foundModel} />
    };
  }

  static isComparisonQuery(message) {
    const keywords = ['compare', 'vs', 'versus', 'difference between', 'which is better'];
    return keywords.some(k => message.includes(k));
  }

  static handleComparisonQuery(message) {
    const models = ['f-150', 'civic', 'rav4', 'x5', 'sierra', 'model 3'];
    const foundModels = models.filter(m => message.includes(m));

    return {
      content: foundModels.length > 0 
        ? `I've prepared a side-by-side comparison for the models you mentioned. You can add more models to compare using the button below:`
        : `I can help you compare our models. Here's a comparison of our most popular inventory to get you started:`,
      component: <ModelComparison initialModels={foundModels} />
    };
  }

  static isDirectInventoryQuery(message) {
    const stockKeywords = ['in stock', 'available', 'have any', 'do you have', 'stock of', 'inventory of'];
    const modelKeywords = ['f-150', 'civic', 'rav4', 'x5', 'sierra', 'model 3'];
    const hasStockKeyword = stockKeywords.some(keyword => message.includes(keyword));
    const hasModelKeyword = modelKeywords.some(model => message.includes(model));
    const isSimpleModelQuery = modelKeywords.some(model => {
      const modelPattern = new RegExp(`\\b${model}\\b`, 'i');
      return modelPattern.test(message) && (message.includes('show') || message.includes('see') || message.length < 20);
    });
    return (hasStockKeyword && hasModelKeyword) || isSimpleModelQuery;
  }

  static handleDirectInventoryQuery(message) {
    let filters = {};
    const models = ['f-150', 'civic', 'rav4', 'x5', 'sierra', 'model 3'];
    const foundModel = models.find(model => message.includes(model));
    if (foundModel) filters.model = foundModel;

    const priceMatch = message.match(/under \$?(\d+,?\d*)/);
    if (priceMatch) filters.maxPrice = parseInt(priceMatch[1].replace(',', ''));

    const filteredVehicles = getVehiclesByFilter(filters);
    
    if (filteredVehicles.length === 0) {
      return {
        content: `We don't currently have that model in stock matching your criteria. Let me show you similar available options:`,
        component: (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {vehicles.slice(0, 3).map(vehicle => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        )
      };
    }

    return {
      content: `Here are the vehicles we have in stock matching your request:`,
      component: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {filteredVehicles.map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      )
    };
  }

  static isRecommendationQuery(message) {
    const keywords = ['recommend', 'suggestion', 'best', 'right', 'perfect', 'help me choose', 'what should'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isServiceAdviceQuery(message) {
    const keywords = ['advice', 'problem', 'issue', 'sound', 'noise', 'warning', 'maintenance', 'check'];
    return keywords.some(keyword => message.includes(keyword)) && !message.includes('appointment');
  }

  static isTrustQuery(message) {
    const keywords = ['trust', 'reliable', 'reputation', 'reviews', 'testimonials', 'credible', 'legitimate'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isPricingQuery(message) {
    const keywords = ['price match', 'best price', 'guarantee', 'lowest price', 'deal', 'discount'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isLeadCaptureQuery(message) {
    const keywords = ['interested', 'want to know more', 'contact me', 'call me', 'email me', 'more information'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isInventoryQuery(message) {
    const keywords = ['show', 'find', 'search', 'inventory', 'vehicles', 'cars', 'suv', 'truck', 'sedan', 'browse', 'looking for'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isVehicleInfoQuery(message) {
    const models = ['f-150', 'civic', 'rav4', 'x5', 'sierra', 'model 3'];
    return models.some(model => message.includes(model)) && 
           (message.includes('tell') || message.includes('about') || message.includes('info') || message.includes('features'));
  }

  static isServiceQuery(message) {
    const keywords = ['service', 'appointment', 'book', 'schedule'];
    return keywords.some(keyword => message.includes(keyword)) && !message.includes('advice');
  }

  static isFinanceQuery(message) {
    const keywords = ['finance', 'financing', 'loan', 'payment', 'calculator', 'trade', 'trade-in', 'credit', 'lease'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isDealershipInfoQuery(message) {
    const keywords = ['hours', 'location', 'address', 'contact', 'phone', 'where', 'when', 'open', 'closed'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static isSpecialsQuery(message) {
    const keywords = ['special', 'specials', 'deal', 'deals', 'promotion', 'discount', 'offer'];
    return keywords.some(keyword => message.includes(keyword));
  }

  static handleSmartRecommendations() {
    return {
      content: "I can provide personalized vehicle recommendations based on your specific needs. Plus, see why thousands of customers trust Carson Exports:",
      component: (
        <div className="space-y-6">
          <SmartRecommendations />
          <TrustSignals />
        </div>
      )
    };
  }

  static handleServiceAdvice() {
    return {
      content: "I can help diagnose potential issues and provide maintenance advice. Here's what our customers say about our service:",
      component: (
        <div className="space-y-6">
          <ServiceAdvisor />
          <CustomerTestimonials />
        </div>
      )
    };
  }

  static handleTrustQuery() {
    return {
      content: "Here's why Carson Exports is the top-rated used car dealer in Dartmouth:",
      component: (
        <div className="space-y-6">
          <TrustSignals />
          <CustomerTestimonials />
        </div>
      )
    };
  }

  static handlePricingQuery() {
    return {
      content: "We guarantee the best value in Nova Scotia. Here are our commitments to you:",
      component: (
        <div className="space-y-6">
          <PriceMatchGuarantee />
          <LeadCaptureIncentives />
        </div>
      )
    };
  }

  static handleLeadCapture() {
    return {
      content: "I'd love to provide you with personalized assistance. Here's what you get when you connect with Carson Exports:",
      component: (
        <div className="space-y-6">
          <LeadCaptureIncentives />
          <LiveInventoryCounter />
        </div>
      )
    };
  }

  static handleInventoryQuery(message) {
    let filters = {};
    const priceMatch = message.match(/under \$?(\d+,?\d*)/);
    if (priceMatch) filters.maxPrice = parseInt(priceMatch[1].replace(',', ''));
    if (message.includes('suv')) filters.type = 'suv';
    else if (message.includes('truck')) filters.type = 'truck';
    else if (message.includes('sedan')) filters.type = 'sedan';

    const filteredVehicles = getVehiclesByFilter(filters);
    const displayVehicles = filteredVehicles.slice(0, 3);

    return {
      content: `I found ${filteredVehicles.length} vehicles matching your criteria at Carson Exports. Here are the top results:`,
      component: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayVehicles.map(vehicle => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
          <LiveInventoryCounter />
          <LeadCaptureIncentives />
        </div>
      )
    };
  }

  static async handleVehicleInfoWithAI(message) {
    try {
      const aiResponse = await openaiService.generateResponse(message);
      return {
        content: aiResponse,
        component: (
          <div className="space-y-6">
            <CustomerTestimonials />
            <LeadCaptureIncentives />
          </div>
        )
      };
    } catch (error) {
      return {
        content: "I'd be happy to tell you more about our inventory. Which specific model are you interested in?",
        component: <LeadCaptureIncentives />
      };
    }
  }

  static handleServiceQuery() {
    return {
      content: "I'd be happy to help you schedule a service appointment at our Dartmouth facility.",
      component: (
        <div className="space-y-6">
          <ServiceBooking />
          <CustomerTestimonials />
        </div>
      )
    };
  }

  static handleFinanceQuery(message) {
    // Extract potential price for initial value
    const priceMatch = message.match(/\$?(\d+,?\d*)/);
    const initialPrice = priceMatch ? parseInt(priceMatch[1].replace(',', '')) : 45000;

    return {
      content: "Explore our flexible financing options. Our team works with all credit types to get you behind the wheel.",
      component: (
        <div className="space-y-6">
          <LoanCalculator initialPrice={initialPrice} />
          <PriceMatchGuarantee />
        </div>
      )
    };
  }

  static handleDealershipInfo() {
    return {
      content: "Here's our information for Carson Exports in Dartmouth:",
      component: (
        <div className="space-y-6">
          <DealershipInfo />
          <TrustSignals />
        </div>
      )
    };
  }

  static async handleSpecialsWithAI(message) {
    try {
      const aiResponse = await openaiService.generateResponse(message);
      return {
        content: aiResponse,
        component: (
          <div className="space-y-6">
            <LiveInventoryCounter />
            <LeadCaptureIncentives />
          </div>
        )
      };
    } catch (error) {
      return {
        content: "Check out our latest specials and inventory deals below:",
        component: <LiveInventoryCounter />
      };
    }
  }

  static async getAIResponse(message) {
    try {
      const aiResponse = await openaiService.generateResponse(message);
      return {
        content: aiResponse,
        component: <LeadCaptureIncentives />
      };
    } catch (error) {
      return {
        content: "Welcome to Carson Exports! How can I help you find your next vehicle today?",
        component: <LeadCaptureIncentives />
      };
    }
  }
}

export default AIResponseHandler;